import { trackDownload } from '../../utils/downloadIndicator';
import React, { useState, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  Divider,
  Grid,
  MenuItem,
  Checkbox,
  FormControlLabel,
  useTheme,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PublicPageShell from '../../components/PublicPageShell';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addStruxorPdfFooter, reservePdfFooterSpace } from '../../utils/pdfBranding';

const PRIMARY = '#0087c1';

const UNITS = ['each', 'm', 'm²', 'm³', 'kg', 'tonne', 'lot', 'pack'];

const DEFAULT_PAYMENT_TERMS = '30 days net from date of invoice.';

const genPONumber = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `PO-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${Math.floor(Math.random() * 900 + 100)}`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const plus14 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
};

let nextLineId = 2;

const emptyLine = (id) => ({ id, description: '', qty: '1', unit: 'each', unitPrice: '', total: 0 });

const fmt = (n) =>
  Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PurchaseOrderTemplate() {
  const theme = useTheme();

  // Company details
  const [company, setCompany] = useState({ name: '', address: '', phone: '', email: '' });

  // Supplier details
  const [supplier, setSupplier] = useState({ name: '', address: '', contactName: '', phone: '', email: '' });

  // PO meta
  const [poMeta, setPoMeta] = useState({
    number: genPONumber(),
    date: todayISO(),
    deliveryDate: plus14(),
  });

  // Delivery address
  const [sameAsCompany, setSameAsCompany] = useState(true);
  const [deliveryAddress, setDeliveryAddress] = useState('');

  // Project reference
  const [projectRef, setProjectRef] = useState('');

  // Line items
  const [lines, setLines] = useState([emptyLine(1)]);

  // VAT
  const [vatRate, setVatRate] = useState('20');

  // Additional fields
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [paymentTerms, setPaymentTerms] = useState(DEFAULT_PAYMENT_TERMS);
  const [specialNotes, setSpecialNotes] = useState('');

  // ───── derived totals ─────
  const computedLines = lines.map((l) => {
    const qty = parseFloat(l.qty) || 0;
    const price = parseFloat(l.unitPrice) || 0;
    return { ...l, total: qty * price };
  });
  const subtotal = computedLines.reduce((s, l) => s + l.total, 0);
  const vatPct = parseFloat(vatRate) || 0;
  const vatAmount = subtotal * (vatPct / 100);
  const grandTotal = subtotal + vatAmount;

  // ───── line-item helpers ─────
  const addLine = () => setLines((prev) => [...prev, emptyLine(nextLineId++)]);
  const removeLine = (id) => setLines((prev) => prev.filter((l) => l.id !== id));

  const updateLine = (id, field, value) => {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  };

  const resolvedDeliveryAddress = sameAsCompany ? company.address : deliveryAddress;

  // ───── PDF generation ─────
  const downloadPDF = useCallback(async () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // Header — company name
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(company.name || 'Your Company Name', 14, y);
    y += 8;

    // Company address block
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    if (company.address) { doc.text(company.address, 14, y); y += 4; }
    if (company.phone) { doc.text(`Tel: ${company.phone}`, 14, y); y += 4; }
    if (company.email) { doc.text(`Email: ${company.email}`, 14, y); y += 4; }

    // PO title — right side
    y = Math.max(y, 36);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('PURCHASE ORDER', pageWidth - 14, 20, { align: 'right' });

    // PO meta — right side
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const metaX = pageWidth - 14;
    doc.text(`PO #: ${poMeta.number}`, metaX, 28, { align: 'right' });
    doc.text(`Date: ${poMeta.date}`, metaX, 33, { align: 'right' });
    doc.text(`Delivery required: ${poMeta.deliveryDate}`, metaX, 38, { align: 'right' });

    // Divider
    y += 2;
    doc.setDrawColor(200);
    doc.line(14, y, pageWidth - 14, y);
    y += 6;

    // Supplier details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Supplier:', 14, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    if (supplier.name) { doc.text(supplier.name, 14, y); y += 4; }
    if (supplier.contactName) { doc.text(`Attn: ${supplier.contactName}`, 14, y); y += 4; }
    if (supplier.address) { doc.text(supplier.address, 14, y); y += 4; }
    if (supplier.phone) { doc.text(`Tel: ${supplier.phone}`, 14, y); y += 4; }
    if (supplier.email) { doc.text(`Email: ${supplier.email}`, 14, y); y += 4; }

    // Delivery address
    if (resolvedDeliveryAddress) {
      y += 4;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Deliver To:', 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const addrLines = doc.splitTextToSize(resolvedDeliveryAddress, pageWidth - 28);
      doc.text(addrLines, 14, y);
      y += addrLines.length * 4 + 2;
    }

    // Project reference
    if (projectRef.trim()) {
      y += 2;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Project Reference:', 14, y);
      doc.setFont('helvetica', 'normal');
      doc.text(projectRef, 60, y);
      y += 6;
    }

    // Line items table
    y += 4;
    const tableBody = computedLines.map((l) => [
      l.description,
      l.qty,
      l.unit,
      `£${fmt(parseFloat(l.unitPrice) || 0)}`,
      `£${fmt(l.total)}`,
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Item Description', 'Qty', 'Unit', 'Unit Price', 'Total']],
      body: tableBody,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [0, 135, 193], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { halign: 'center', cellWidth: 20 },
        2: { halign: 'center', cellWidth: 20 },
        3: { halign: 'right', cellWidth: 28 },
        4: { halign: 'right', cellWidth: 28 },
      },
      margin: { left: 14, right: 14, bottom: 24 },
    });

    y = doc.lastAutoTable.finalY + 6;

    // Totals
    y = reservePdfFooterSpace(doc, y, 18);
    const totalsX = pageWidth - 14;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Subtotal:', totalsX - 40, y);
    doc.text(`£${fmt(subtotal)}`, totalsX, y, { align: 'right' });
    y += 5;
    doc.text(`VAT (${vatPct}%):`, totalsX - 40, y);
    doc.text(`£${fmt(vatAmount)}`, totalsX, y, { align: 'right' });
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Grand Total:', totalsX - 40, y);
    doc.text(`£${fmt(grandTotal)}`, totalsX, y, { align: 'right' });

    // Delivery instructions
    if (deliveryInstructions.trim()) {
      const instrLines = doc.splitTextToSize(deliveryInstructions, pageWidth - 28);
      y = reservePdfFooterSpace(doc, y + 10, instrLines.length * 3.5 + 8);
      y += 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Delivery Instructions', 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(instrLines, 14, y);
      y += instrLines.length * 3.5 + 2;
    }

    // Payment terms
    if (paymentTerms.trim()) {
      const termLines = doc.splitTextToSize(paymentTerms, pageWidth - 28);
      y = reservePdfFooterSpace(doc, y + 4, termLines.length * 3.5 + 8);
      y += 4;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Payment Terms', 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(termLines, 14, y);
      y += termLines.length * 3.5 + 2;
    }

    // Special notes
    if (specialNotes.trim()) {
      const noteLines = doc.splitTextToSize(specialNotes, pageWidth - 28);
      y = reservePdfFooterSpace(doc, y + 4, noteLines.length * 3.5 + 8);
      y += 4;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Special Instructions / Notes', 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(noteLines, 14, y);
    }

    await addStruxorPdfFooter(doc);
    doc.save(`${poMeta.number}.pdf`);
  }, [company, supplier, poMeta, projectRef, resolvedDeliveryAddress, computedLines, subtotal, vatPct, vatAmount, grandTotal, deliveryInstructions, paymentTerms, specialNotes]);

  // ───── section header helper ─────
  const SectionTitle = ({ children }) => (
    <Typography variant="h6" fontWeight={700} sx={{ color: PRIMARY, mb: 2 }}>
      {children}
    </Typography>
  );

  // ───── render ─────
  return (
    <>
      <Helmet>
        <title>Free Construction Purchase Order Template — Create &amp; Download PDF | Struxor</title>
        <meta
          name="description"
          content="Free construction purchase order template. Fill in your company and supplier details, add line items, and download a professional PO as PDF. No sign-up required."
        />
        <link rel="canonical" href="https://www.struxorapp.com/tools/purchase-order-template" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'Construction Purchase Order Template',
          description: 'Free online construction purchase order template. Create professional POs with line items, VAT calculations, and download as PDF.',
          url: 'https://www.struxorapp.com/tools/purchase-order-template',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' },
          author: { '@type': 'Organization', name: 'Struxor', url: 'https://www.struxorapp.com' },
        })}</script>
      </Helmet>

      <PublicPageShell>

      {/* ── Hero ── */}
      <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #005a8c 100%)`, color: '#fff', py: 6, px: 2, textAlign: 'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" mb={2}>
          <ShoppingCartIcon sx={{ fontSize: 36 }} />
          <Typography variant="h4" component="h1" fontWeight={700}>
            Construction Purchase Order Template
          </Typography>
        </Stack>
        <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 640, mx: 'auto', fontWeight: 400 }}>
          Create professional purchase orders for materials and services, then download as PDF. Free, no sign-up required.
        </Typography>
      </Box>

      <Container maxWidth="lg" sx={{ py: 5 }}>

        {/* ══════════ Company Details ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Your Company Details</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Company Name" size="small" value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" size="small" value={company.email} onChange={(e) => setCompany({ ...company, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Address" size="small" value={company.address} onChange={(e) => setCompany({ ...company, address: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone" size="small" value={company.phone} onChange={(e) => setCompany({ ...company, phone: e.target.value })} />
            </Grid>
          </Grid>
        </Paper>

        {/* ══════════ Supplier Details ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Supplier / Vendor Details</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Supplier Name" size="small" value={supplier.name} onChange={(e) => setSupplier({ ...supplier, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Contact Name" size="small" value={supplier.contactName} onChange={(e) => setSupplier({ ...supplier, contactName: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Supplier Address" size="small" value={supplier.address} onChange={(e) => setSupplier({ ...supplier, address: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Supplier Phone" size="small" value={supplier.phone} onChange={(e) => setSupplier({ ...supplier, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Supplier Email" size="small" value={supplier.email} onChange={(e) => setSupplier({ ...supplier, email: e.target.value })} />
            </Grid>
          </Grid>
        </Paper>

        {/* ══════════ PO Meta ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Purchase Order Details</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="PO Number" size="small" value={poMeta.number} onChange={(e) => setPoMeta({ ...poMeta, number: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Date" size="small" type="date" InputLabelProps={{ shrink: true }} value={poMeta.date} onChange={(e) => setPoMeta({ ...poMeta, date: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Delivery Date Required" size="small" type="date" InputLabelProps={{ shrink: true }} value={poMeta.deliveryDate} onChange={(e) => setPoMeta({ ...poMeta, deliveryDate: e.target.value })} />
            </Grid>
          </Grid>
        </Paper>

        {/* ══════════ Delivery Address & Project Reference ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Delivery &amp; Project</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sameAsCompany}
                    onChange={(e) => setSameAsCompany(e.target.checked)}
                    sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }}
                  />
                }
                label="Delivery address same as company address"
              />
            </Grid>
            {!sameAsCompany && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Delivery Address"
                  size="small"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="Enter the site or delivery address"
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Reference"
                size="small"
                value={projectRef}
                onChange={(e) => setProjectRef(e.target.value)}
                placeholder="e.g. Project name or job number"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ══════════ Line Items ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, mb: 3, overflow: 'hidden' }}>
          <Box sx={{ background: alpha(PRIMARY, 0.06), px: 3, py: 2 }}>
            <SectionTitle>Line Items</SectionTitle>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: theme.palette.background.default }}>
                  <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>Item Description</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 80 }} align="center">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 90 }} align="center">Unit</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 110 }} align="right">Unit Price (£)</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 110 }} align="right">Total (£)</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {computedLines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g. Portland cement CEM II 25kg bags"
                        value={line.description}
                        onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                        sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 'any' }}
                        value={line.qty}
                        onChange={(e) => updateLine(line.id, 'qty', e.target.value)}
                        sx={{ width: 70, '& .MuiInputBase-input': { fontSize: 13, textAlign: 'center' } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        select
                        size="small"
                        value={line.unit}
                        onChange={(e) => updateLine(line.id, 'unit', e.target.value)}
                        sx={{ width: 80, '& .MuiInputBase-input': { fontSize: 13 } }}
                      >
                        {UNITS.map((u) => (
                          <MenuItem key={u} value={u}>{u}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 'any' }}
                        placeholder="0.00"
                        value={line.unitPrice}
                        onChange={(e) => updateLine(line.id, 'unitPrice', e.target.value)}
                        sx={{ width: 100, '& .MuiInputBase-input': { fontSize: 13, textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        £{fmt(line.total)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {lines.length > 1 && (
                        <IconButton size="small" onClick={() => removeLine(line.id)} sx={{ color: theme.palette.error.main }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Button startIcon={<AddIcon />} onClick={addLine} variant="outlined" sx={{ borderColor: PRIMARY, color: PRIMARY, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
              Add Line Item
            </Button>

            {/* Totals */}
            <Box sx={{ minWidth: 260 }}>
              <Stack direction="row" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2" fontWeight={600}>£{fmt(subtotal)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5} spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2">VAT</Typography>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 'any' }}
                    value={vatRate}
                    onChange={(e) => setVatRate(e.target.value)}
                    sx={{ width: 64, '& .MuiInputBase-input': { fontSize: 13, textAlign: 'center', py: '4px' } }}
                  />
                  <Typography variant="body2">%:</Typography>
                </Stack>
                <Typography variant="body2" fontWeight={600}>£{fmt(vatAmount)}</Typography>
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: PRIMARY }}>Grand Total:</Typography>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: PRIMARY }}>£{fmt(grandTotal)}</Typography>
              </Stack>
            </Box>
          </Box>
        </Paper>

        {/* ══════════ Delivery Instructions ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Delivery Instructions</SectionTitle>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder="e.g. Deliver to site entrance on Main Street. Contact site manager on arrival. Forklift available for offloading."
            value={deliveryInstructions}
            onChange={(e) => setDeliveryInstructions(e.target.value)}
            size="small"
          />
        </Paper>

        {/* ══════════ Payment Terms ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Payment Terms</SectionTitle>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={paymentTerms}
            onChange={(e) => setPaymentTerms(e.target.value)}
            size="small"
          />
        </Paper>

        {/* ══════════ Special Instructions / Notes ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 4 }}>
          <SectionTitle>Special Instructions / Notes</SectionTitle>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder="Any additional notes, specifications, or special conditions…"
            value={specialNotes}
            onChange={(e) => setSpecialNotes(e.target.value)}
            size="small"
          />
        </Paper>

        {/* ══════════ Download Button ══════════ */}
        <Stack direction="row" spacing={2} mb={6} justifyContent="center">
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => trackDownload(downloadPDF, { defer: true })()}
            variant="contained"
            size="large"
            sx={{ background: PRIMARY, '&:hover': { background: '#006fa0' }, textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 5 }}
          >
            Download PDF
          </Button>
        </Stack>

        {/* ══════════ Tips ══════════ */}
        <Box mb={5}>
          <Stack direction="row" spacing={1} alignItems="center" mb={3}>
            <LightbulbIcon sx={{ color: PRIMARY }} />
            <Typography variant="h5" fontWeight={700}>Tips for Effective Purchase Order Management</Typography>
          </Stack>
          <Grid container spacing={3}>
            {[
              { title: 'Always Use PO Numbers', body: 'Assign a unique PO number to every order. This creates a clear audit trail, simplifies invoice matching, and helps resolve disputes with suppliers quickly.' },
              { title: 'Specify Delivery Dates', body: 'Always include a required delivery date. Late materials cause costly programme delays on site — a clear date gives you leverage to chase or claim.' },
              { title: 'Include the Project Reference', body: 'Link every PO to a specific project or job number. This makes it easy to track costs per project and keeps your budget reporting accurate.' },
              { title: 'Detail Specifications Clearly', body: 'Be precise about what you are ordering — include sizes, grades, colours, and standards. Vague descriptions lead to wrong deliveries and wasted time.' },
              { title: 'Confirm Lead Times', body: 'Before raising a PO, confirm the supplier\'s lead time. Long lead items like steelwork or bespoke joinery can delay a project if not ordered early enough.' },
              { title: 'Keep Records of Every PO', body: 'Store all purchase orders centrally. Good records protect you during audits, help with cash flow forecasting, and provide evidence if a supplier disputes an order.' },
            ].map(({ title, body }) => (
              <Grid item xs={12} sm={6} md={4} key={title}>
                <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2.5, height: '100%' }}>
                  <Typography fontWeight={700} mb={1}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary">{body}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ══════════ How Struxor Extends This ══════════ */}
        <Paper elevation={0} sx={{ border: `2px solid ${PRIMARY}`, borderRadius: 3, p: { xs: 3, md: 4 }, mb: 5 }}>
          <Typography variant="h5" fontWeight={700} mb={1}>
            How Struxor Extends Your Purchase Order Management
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            This free template gets the job done — but Struxor's built-in purchase order system takes procurement management to the next level.
          </Typography>
          <Grid container spacing={2}>
            {[
              'Purchase orders linked directly to projects',
              'System pre-populates known fields to save time',
              'Select saved items and preivously used suppliers',
              'Full order history and audit trail for every supplier',
            ].map((item) => (
              <Grid item xs={12} sm={6} key={item}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                  <Typography variant="body2">{item}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
          <Box mt={3}>
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              sx={{ background: PRIMARY, '&:hover': { background: '#006fa0' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Try Struxor Free for 14 Days
            </Button>
          </Box>
        </Paper>

        {/* ══════════ FAQ ══════════ */}
        <Box mb={5}>
          <Typography variant="h5" fontWeight={700} mb={3}>Frequently Asked Questions</Typography>
          <Grid container spacing={3}>
            {[
              {
                q: 'Why should I use purchase orders in construction?',
                a: 'Purchase orders create a formal, written record of what was ordered, the agreed price, and the delivery date. They protect both you and the supplier, prevent misunderstandings, simplify invoice matching, and give you a clear audit trail for project costs.',
              },
              {
                q: 'What is the difference between a purchase order and an invoice?',
                a: 'A purchase order (PO) is issued by the buyer before goods or services are delivered — it is a request to supply. An invoice is issued by the supplier after delivery — it is a request for payment. The PO number links the two documents together.',
              },
              {
                q: 'How do I track PO spend against my project budget?',
                a: 'Record every PO against the relevant project or cost code. Sum your committed PO values and compare them to your budget. This shows you how much of your budget is already spoken for, even before invoices arrive. Struxor does this automatically.',
              },
              {
                q: 'What should I do when actual costs exceed the PO amount?',
                a: 'If a supplier delivers more than ordered or prices change, issue a revised PO or a supplementary PO for the difference. Never accept charges that exceed the original PO without a documented change — this protects your budget and provides an audit trail.',
              },
            ].map(({ q, a }) => (
              <Grid item xs={12} md={6} key={q}>
                <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2.5 }}>
                  <Typography fontWeight={700} mb={1}>{q}</Typography>
                  <Typography variant="body2" color="text.secondary">{a}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        

        {/* ══════════ Footer Links ══════════ */}
        <Divider sx={{ mb: 3 }} />
        <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap" mb={4}>
          <Button component={RouterLink} to="/tools/quote-template" variant="text" sx={{ textTransform: 'none', color: PRIMARY }}>
            → Quote Template
          </Button>
          <Button component={RouterLink} to="/tools/invoice-template" variant="text" sx={{ textTransform: 'none', color: PRIMARY }}>
            → Invoice Template
          </Button>
          <Button component={RouterLink} to="/" variant="text" sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Back to Struxor Home
          </Button>
        </Stack>
      </Container>
      </PublicPageShell>
    </>
  );
}
